import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

/**
 * Sohbet composer'ının metin alanı — `<textarea>` yerine contenteditable.
 *
 * NEDEN: belge etiketleri (`[etiket](/yol)`) yazı kutusunda uzun ham markdown
 * yerine, gönderilen mesajdaki GİBİ (richText.jsx) satır içi ÇİP olarak görünsün
 * (ClickUp deseni, kullanıcı talebi 23.09.2026).
 *
 * TASARIM — textarea'nın DIŞ ARAYÜZÜNÜ taklit eder ki Composer'ın mevcut
 * mention/caret/gönder/düzenle mantığı DEĞİŞMEDEN çalışsın:
 *   ref.value · ref.selectionStart · ref.selectionEnd · ref.setSelectionRange(a,b)
 *   ref.focus() · ref.getBoundingClientRect()
 *   onChange({ target: <bu arayüz> })   // target.value + target.selectionStart okunur
 *
 * MODEL DÜZ METİNDİR: DOM her zaman `value` string'ine serialize edilir; çip'ler
 * yalnız GÖRÜNÜMdür (data-token ile tam token'ı taşır). Böylece backend'e,
 * mention tespitine ve gönderime giden şey eskisiyle birebir aynı metindir.
 *
 * KONTROLLÜ ama caret KORUNUR: düz yazımda DOM zaten `value`'ya eşit serialize
 * olduğu için yeniden KURULMAZ (native caret). Yalnız DIŞ programatik değişimde
 * (mention ekleme, düzenleme yükleme, emoji, temizleme) DOM yeniden kurulur ve
 * caret istenen offset'e taşınır.
 *
 * KAPSAM: yalnız belge/kanal bağlantısı `[etiket](/yol)` çiplenir — o token
 * panelden HER ZAMAN tam eklenir, elle karakter karakter yazılmaz → erken
 * çipleme sorunu olmaz. `@kişi` / `@@görev` / `#kanal` düz metin kalır (kısa ve
 * okunur; asıl şikâyet uzun URL'ydi). İstenirse aynı desenle genişletilebilir.
 */

// richText.jsx ile AYNI kural: yalnız uygulama-içi (`/...`) bağlantı çip olur.
const DOC_RE = /\[([^\]]+)\]\((\/[^)\s]+)\)/g
// lucide FileText ile aynı görünüm (imperatif DOM'da React ikonu kullanılamaz)
const FILE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" ' +
  'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
  'stroke-linejoin="round" style="flex:none"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>'

function makeChip(token, label) {
  const span = document.createElement('span')
  span.setAttribute('contenteditable', 'false')
  span.dataset.token = token
  span.title = label
  span.className =
    'inline-flex items-center gap-1 px-1.5 py-0.5 mx-[1px] rounded bg-amber-50 ' +
    'text-amber-800 text-[12px] font-medium align-middle select-none'
  span.innerHTML = FILE_SVG + '<span></span>'
  span.lastChild.textContent = label
  return span
}

// Sondaki tek '\n' contenteditable'da (pre-wrap) boş satır olarak ÇİZİLMEZ — Shift+Enter
// "hiçbir şey yapmıyor" görünüyordu. Metin '\n' ile bitiyorsa sona görünmez bir <br>
// (bitiş işareti) konur; metne SAYILMAZ (serialize/nodeLen 0).
const isEndMark = (n) => n.nodeName === 'BR' && n.dataset && n.dataset.end != null

function syncEndMark(el) {
  // Ortada kalmış işaret (imleç işaretin arkasına düşüp yazılmışsa) görünür satır sonu
  // olur ama metne sayılmaz → kaldırılır; işaret yalnız en sonda yaşar.
  el.querySelectorAll('br[data-end]').forEach((b) => { if (b !== el.lastChild) b.remove() })
  const last = el.lastChild
  const mark = last && isEndMark(last) ? last : null
  const wants = serialize(el).endsWith('\n')
  if (wants && !mark) {
    const br = document.createElement('br')
    br.dataset.end = ''
    el.appendChild(br)
  } else if (!wants && mark) {
    mark.remove()
  }
}

/** Bir düğümün DÜZ METİN uzunluğu (çip = token uzunluğu). */
function nodeLen(n) {
  if (isEndMark(n)) return 0
  if (n.nodeType === 3) return n.nodeValue.length
  if (n.dataset && n.dataset.token != null) return n.dataset.token.length
  if (n.nodeName === 'BR') return 1
  return (n.textContent || '').length
}

/** contenteditable DOM → düz metin (çipler token'a geri döner). */
function serialize(el) {
  let out = ''
  el.childNodes.forEach((n) => {
    if (n.nodeType === 3) out += n.nodeValue
    else if (isEndMark(n)) { /* bitiş işareti — metin değil */ }
    else if (n.nodeName === 'BR') out += '\n'
    else if (n.dataset && n.dataset.token != null) out += n.dataset.token
    else out += n.textContent || ''
  })
  return out
}

/** Düz metin → DOM (çip + metin), el temizlenip yeniden kurulur. */
function buildInto(el, text) {
  el.textContent = ''
  const frag = document.createDocumentFragment()
  let last = 0
  let m
  DOC_RE.lastIndex = 0
  while ((m = DOC_RE.exec(text))) {
    if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)))
    frag.appendChild(makeChip(m[0], m[1]))
    last = m.index + m[0].length
  }
  if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)))
  el.appendChild(frag)
  syncEndMark(el)
}

/** Düz metin offset'i → DOM konumu (Range için). */
function placeCaret(el, offset) {
  const sel = window.getSelection()
  if (!sel) return
  const r = document.createRange()
  let remaining = Math.max(0, offset)
  let placed = false
  for (const n of el.childNodes) {
    const len = nodeLen(n)
    if (n.nodeType === 3) {
      if (remaining <= len) { r.setStart(n, remaining); placed = true; break }
    } else {
      // çip / br — içine girilemez: sınıra yerleş
      if (remaining <= 0) { r.setStartBefore(n); placed = true; break }
      if (remaining <= len) { r.setStartAfter(n); placed = true; break }
    }
    remaining -= len
  }
  if (!placed) { r.selectNodeContents(el); r.collapse(false) }
  else r.collapse(true)
  sel.removeAllRanges()
  sel.addRange(r)
}

/** DOM seçim konumu → düz metin offset'i. */
function caretOffset(el) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return serialize(el).length
  const r = sel.getRangeAt(0)
  const c = r.startContainer
  if (!el.contains(c) && c !== el) return serialize(el).length
  // Seçim kapsayıcının KENDİSİndeyse (childIndex) → o indekse kadarki uzunluk
  if (c === el) {
    let o = 0
    let i = 0
    for (const n of el.childNodes) { if (i >= r.startOffset) break; o += nodeLen(n); i++ }
    return o
  }
  // Seçim bir alt düğümde (genelde metin düğümü)
  let o = 0
  for (const n of el.childNodes) {
    if (n === c) return o + r.startOffset
    if (n.nodeType === 1 && n.contains(c)) return o   // çip içi (olmamalı) — çip başı
    o += nodeLen(n)
  }
  return o
}

const RichTextInput = forwardRef(function RichTextInput(
  { value = '', onChange, onKeyDown, placeholder, className = '', rows = 3, ...rest }, ref,
) {
  const elRef = useRef(null)
  const composingRef = useRef(false)

  const emitChange = () => {
    const el = elRef.current
    if (!el || !onChange) return
    onChange({ target: apiRef.current })
  }

  // textarea benzeri imperatif arayüz — Composer bunu taRef olarak kullanır
  const apiRef = useRef(null)
  if (!apiRef.current) {
    apiRef.current = {
      get value() { return elRef.current ? serialize(elRef.current) : '' },
      get selectionStart() { return elRef.current ? caretOffset(elRef.current) : 0 },
      get selectionEnd() {
        const el = elRef.current; if (!el) return 0
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return caretOffset(el)
        // end = başlangıç + seçili uzunluk (çoğunlukla daraltılmış)
        const r = sel.getRangeAt(0)
        if (r.collapsed) return caretOffset(el)
        return caretOffset(el) + String(r).length
      },
      setSelectionRange(a) {
        const el = elRef.current; if (!el) return
        el.focus()
        placeCaret(el, a)
      },
      focus() { elRef.current?.focus() },
      getBoundingClientRect() { return elRef.current?.getBoundingClientRect() },
    }
  }
  useImperativeHandle(ref, () => apiRef.current, [])

  // DIŞ değişimi DOM'a uygula — düz yazımda (DOM zaten value'ya eşit) DOKUNMA.
  useLayoutEffect(() => {
    const el = elRef.current
    if (!el) return
    if (serialize(el) === value) return
    const focused = document.activeElement === el
    buildInto(el, value)
    if (focused) placeCaret(el, value.length)   // dış ekleme sonrası; mention akışı setSelectionRange ile düzeltir
  }, [value])

  const handleKeyDown = (e) => {
    // Shift+Enter → satır sonu (contenteditable tutarlılığı için elle '\n')
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault()
      const el = elRef.current
      const sel = window.getSelection()
      if (el && sel && sel.rangeCount) {
        const r = sel.getRangeAt(0)
        r.deleteContents()
        const tn = document.createTextNode('\n')
        r.insertNode(tn)
        syncEndMark(el)
        r.setStartAfter(tn); r.collapse(true)
        sel.removeAllRanges(); sel.addRange(r)
        emitChange()
      }
      return
    }
    onKeyDown?.(e)
  }

  return (
    <div
      ref={elRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      data-placeholder={placeholder || ''}
      onInput={() => { if (elRef.current) syncEndMark(elRef.current); if (!composingRef.current) emitChange() }}
      onCompositionStart={() => { composingRef.current = true }}
      onCompositionEnd={() => { composingRef.current = false; emitChange() }}
      onKeyDown={handleKeyDown}
      className={`vx-rich-input whitespace-pre-wrap break-words overflow-y-auto ${className}`}
      style={{ minHeight: `${(rows || 3) * 20 + 16}px`, maxHeight: '40vh' }}
      {...rest}
    />
  )
})

export default RichTextInput
